import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Download, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const companyInfo = {
  name: "NJK Logistics Pte Ltd",
  address: "212A Punggol Walk #12-741 Singapore 821212",
  phone: "98366186",
  email: "inquiry@njklogistics.com",
  uen: "202606027K"
};

export const PayslipDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payslip, setPayslip] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    fetchPayslip();
  }, [id]);

  const fetchPayslip = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/payslips/${id}`);
      setPayslip(res.data);
    } catch (error) {
      toast.error('Failed to load payslip');
      navigate('/admin/payslip');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-SG', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 px-4 flex justify-center items-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!payslip) {
    return (
      <div className="min-h-screen pt-32 px-4 flex justify-center items-center">
        <p>Payslip not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-44 pb-12 px-4 bg-gray-50">
      <div className="container mx-auto max-w-4xl">
        {/* Action Bar - Hidden when printing */}
        <div className="flex items-center justify-between gap-4 mb-8 print:hidden">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/payslip')}>
              <ArrowLeft size={16} className="mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payslip</h1>
              <p className="text-gray-600">{payslip.pay_period}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" data-testid="download-payslip-pdf-btn">
              <a href={`${BACKEND_URL}/api/payslips/${id}/pdf`} target="_blank" rel="noreferrer">
                <FileDown size={16} className="mr-2" />
                Download PDF
              </a>
            </Button>
            <Button onClick={handlePrint}>
              <Printer size={16} className="mr-2" />
              Print Payslip
            </Button>
          </div>
        </div>

        {/* Payslip Document */}
        <div ref={printRef} className="bg-white rounded-lg shadow-lg p-8 print:shadow-none print:p-4">
          {/* Header */}
          <div className="border-b-2 border-blue-600 pb-6 mb-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-blue-600">{companyInfo.name}</h1>
                <p className="text-gray-600">{companyInfo.address}</p>
                <p className="text-gray-600">Tel: {companyInfo.phone}</p>
                <p className="text-gray-600">UEN: {companyInfo.uen}</p>
              </div>
              <div className="text-right">
                <h2 className="text-xl font-bold">PAYSLIP</h2>
                <p className="text-gray-600">Pay Period: {payslip.pay_period}</p>
                <p className="text-gray-600">Pay Date: {formatDate(payslip.pay_date)}</p>
              </div>
            </div>
          </div>

          {/* Employee Details */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="font-semibold text-gray-800 mb-2">Employee Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p><span className="text-gray-600">Name:</span> <strong>{payslip.employee_name}</strong></p>
                <p><span className="text-gray-600">Position:</span> {payslip.position}</p>
              </div>
            </div>
          </div>

          {/* Earnings & Deductions */}
          <div className="grid grid-cols-2 gap-8 mb-6">
            {/* Earnings */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-4 border-b pb-2">EARNINGS</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Basic Salary</span>
                  <span>{formatCurrency(payslip.basic_salary)}</span>
                </div>
                {payslip.overtime_pay > 0 && (
                  <div className="flex justify-between">
                    <span>Overtime ({payslip.overtime_hours} hrs)</span>
                    <span>{formatCurrency(payslip.overtime_pay)}</span>
                  </div>
                )}
                {payslip.allowances?.map((item, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{item.description}</span>
                    <span>{formatCurrency(item.amount)}</span>
                  </div>
                ))}
                {payslip.bonuses?.map((item, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{item.description}</span>
                    <span>{formatCurrency(item.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-semibold border-t pt-2 mt-4">
                  <span>Gross Pay</span>
                  <span>{formatCurrency(payslip.gross_pay)}</span>
                </div>
              </div>
            </div>

            {/* Deductions */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-4 border-b pb-2">DEDUCTIONS</h3>
              <div className="space-y-2">
                {payslip.cpf_employee > 0 && (
                  <div className="flex justify-between">
                    <span>CPF Employee</span>
                    <span>{formatCurrency(payslip.cpf_employee)}</span>
                  </div>
                )}
                {payslip.other_deductions?.map((item, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{item.description}</span>
                    <span>{formatCurrency(item.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-semibold border-t pt-2 mt-4">
                  <span>Total Deductions</span>
                  <span>{formatCurrency(payslip.total_deductions)}</span>
                </div>
              </div>

              {/* Employer CPF */}
              {payslip.cpf_employer > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between text-gray-600">
                    <span>CPF Employer (for info)</span>
                    <span>{formatCurrency(payslip.cpf_employer)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Net Pay */}
          <div className="bg-green-50 p-6 rounded-lg text-center">
            <p className="text-gray-600 mb-2">NET PAY</p>
            <p className="text-4xl font-bold text-green-600">{formatCurrency(payslip.net_pay)}</p>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t text-center text-gray-500 text-sm">
            <p>This is a computer-generated payslip. No signature is required.</p>
            <p>Generated on {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:hidden {
            display: none !important;
          }
          #root > div > div > div:last-child,
          #root > div > div > div:last-child * {
            visibility: visible;
          }
          #root > div > div > div:last-child {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default PayslipDetail;
